// ============================================================================
// src/app/boot/sessionProfileLoad.ts — the bounded, abortable profile attempt
// ============================================================================
// App's `loadUserImpl` reads the signed-in user's `profiles` row to learn what
// role the session holds. This module owns the ATTEMPT MECHANICS of that read
// — the budget, the abort, the retry cap, the cleanup — and nothing else.
//
// TWO MECHANISMS, AND BOTH ARE LOAD-BEARING
//
// The loop this replaces raced an unsubscribed PostgREST request against a
// 5-second `Promise.race` timer:
//
//   res = await Promise.race([profilePromise, timeoutPromise]);
//
// Losing the race REJECTED the await; it did not cancel the request. On a slow
// mobile connection all three attempts stayed in flight against the same
// PostgREST endpoint while the loop had already given up on each.
//
// The fix is NOT "abort instead of racing". A reader who sees `Promise.race`
// below and concludes the defect came back has it backwards — the defect was
// racing WITHOUT cancelling, and this module does both, because each covers a
// failure the other cannot:
//
//   * THE RACE GIVES LIVENESS. `await request(signal)` is only bounded if the
//     request observes the abort. A request that never even reaches `fetch`
//     never sees the signal, so aborting it does nothing and the await hangs
//     FOREVER. That is not hypothetical: a profile query issued from inside an
//     `onAuthStateChange` callback blocks on the GoTrue auth lock before any
//     request object exists (see the App-side note on deferring intake), and
//     `_acquireLock`'s re-entrant branch has NO acquire timeout — it awaits the
//     very `pendingInLock` chain the callback is running inside. The budget
//     promise rejects on its own timer, so this function settles regardless of
//     what the transport does.
//   * THE ABORT IS BEST-EFFORT CANCELLATION. Rejecting the race abandons the
//     caller's await but leaves the request running unless something cancels
//     it, so every attempt's controller is aborted on EVERY exit before the
//     next attempt is constructed.
//
//     It is NOT a guarantee that no request outlives its attempt. A request
//     that ignores its signal — or that has not yet reached `fetch`, so there
//     is nothing to signal — keeps running, and this module cannot stop it.
//     What IS guaranteed is bounded: the caller always settles, the deadline
//     timer and lease listener are always cleaned up, a late rejection is
//     parked so it cannot surface as unhandled, and no abandoned request is
//     ever awaited again or allowed to publish a result.
//
// Remove either one and a real, previously-observed defect returns.
//
// WHAT DELIBERATELY DID NOT MOVE
//
// Nothing that DECIDES anything about the profile. `loadSessionProfile` returns
// a tagged outcome and never invents a row, a role, or a default. The
// fail-closed catch — `role: 'inactive'`, `setView('home')`,
// `setProfileStatus(PROFILE_STATUS.FAILED)` — stays in src/App.tsx, where
// tests/static/security_client_access_static.test.js reads it out of App's own
// braced source region. A guard that reads source text can only see code still
// in the file it reads, so the privilege decision must not follow the transport
// out of App. `readSessionProfile` (src/lib/sessionProfileApi.ts) still owns
// the PostgREST chain; App still names it. This module sees only a thunk.
//
// ONE BUDGET FOR THE WHOLE RESOLUTION, NOT ONE PER ATTEMPT
//
// The budget is `DEFAULT_SUPABASE_REQUEST_TIMEOUT_MS` from
// src/lib/boundedFetch.ts — the deadline the production Supabase transport
// already applies to every PostgREST and Auth request. The old 5s cap was a
// second, tighter, undocumented budget layered on top of it, and on a
// rural/mobile connection it was the thing that failed the session: the request
// the browser was still happily receiving at 6s had already been declared dead.
//
// It is armed ONCE, for the whole call, rather than per attempt. Three
// independent 20s attempts would put a 60s wait in front of the fail-closed
// banner on a connection that never answers, and a second and third full-length
// timeout buy nothing that the first did not already prove. So:
//
//   * a slow-but-alive request still gets the entire 20s allowance;
//   * a GENUINE TIMEOUT ENDS THE RESOLUTION — no further attempts;
//   * a fast transient failure (a dropped connection, a 5xx) still retries,
//     up to SESSION_PROFILE_ATTEMPTS, but only while budget remains.
//
// Worst case to the banner is therefore the one documented number, 20s, for
// every failure shape.
// ============================================================================

import {DEFAULT_SUPABASE_REQUEST_TIMEOUT_MS} from '../../lib/boundedFetch.js';

/** Retry cap for TRANSIENT failures. A timeout is not transient; it ends the run. */
export const SESSION_PROFILE_ATTEMPTS = 3;

/** The single documented budget, for the whole resolution. See the header. */
export const SESSION_PROFILE_TIMEOUT_MS = DEFAULT_SUPABASE_REQUEST_TIMEOUT_MS;

/**
 * The `source` column value for handled profile-load failures.
 * `client_error_events.source` is capped at 50 characters by migration 068.
 */
export const SESSION_PROFILE_ERROR_SOURCE = 'session.profile-load';

/**
 * What a PostgREST single-row read settles to.
 *
 * `status` is RETAINED rather than discarded because it is the only
 * trustworthy input to the retry classifier: a PostgREST error body carries a
 * code but not the HTTP status, and 503 and 401 are indistinguishable without
 * it. Optional because an injected or faked builder need not supply one, in
 * which case classification falls back to the transport-shape rules.
 */
export type SessionProfileResponse<TProfile> = {data: TProfile | null; error: unknown; status?: number};

/**
 * One attempt's transport. Receives the attempt's signal and must apply it to
 * the request; it is called once per attempt, never reused.
 */
export type SessionProfileRequest<TProfile> = (signal: AbortSignal) => PromiseLike<SessionProfileResponse<TProfile>>;

/**
 * `superseded` is NOT a failure: the lease that owns this run stopped being
 * current, so the caller must publish nothing at all — neither a profile nor a
 * fail-closed role. `failed` carries only what the caller needs to fail closed
 * and to report; it never carries a substitute profile.
 */
export type SessionProfileOutcome<TProfile> =
  | {status: 'resolved'; profile: TProfile}
  | {status: 'superseded'}
  | {status: 'failed'; error: unknown; attempts: number; timedOut: boolean};

export type LoadSessionProfileOptions<TProfile> = {
  request: SessionProfileRequest<TProfile>;
  /** The boot lease's own guard. Checked before every attempt and after every settle. */
  isCurrent: () => boolean;
  /** Raced against, AND composed into each attempt's signal. */
  leaseSignal?: AbortSignal | null;
  attempts?: number;
  timeoutMs?: number;
  /** Injectable so a suite can observe abort without a real network. */
  createController?: () => AbortController;
};

function isObjectLike(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

// ── WHAT IS WORTH RETRYING ─────────────────────────────────────────────────
//
// Retrying a permanent failure is not free: it spends the shared budget, it
// issues load against an endpoint that already answered, and it delays the
// fail-closed banner the operator needs. The earlier loop retried EVERY fast
// error — a missing row, PGRST116, a 401, a malformed response — three times
// over, and none of those can succeed on a second ask.
//
// So the rule is conservative in the safe direction: retry only what is
// RECOGNISABLY transient, and treat everything else — including anything
// unrecognised — as permanent. Misclassifying a transient failure as permanent
// costs one fail-closed banner the operator can dismiss by reloading;
// misclassifying a permanent one as transient costs three round trips and a
// slower banner. Neither can elevate a role, because every path here still
// ends in `failed`.

// Explicitly retryable statuses: request timeout, too-early, rate limit.
// 5xx is handled by range, so a novel server code is still retried.
const RETRYABLE_STATUS = new Set([408, 425, 429]);

// Transport failures that never reached a response. Matched on shape and on
// the messages the bounded transport itself produces (src/lib/boundedFetch.ts),
// not on arbitrary text.
const TRANSPORT_FAILURE_RE =
  /failed to fetch|networkerror|network error|econnreset|econnrefused|etimedout|socket hang up|supabase request (?:failed|timed out) after/i;

/**
 * May this failure be retried inside the remaining budget?
 *
 * An explicit HTTP status is the authority when there is one: 5xx and the
 * three retryable 4xx codes may be retried, and EVERY other status — 400,
 * 401, 403, 404, 406, and anything else a server chose to answer with — is
 * permanent. Without a status the failure never reached a response, so only
 * recognised transport shapes are retried; an unrecognised structured error
 * is permanent.
 */
export function isRetryableProfileFailure(error: unknown, status?: number): boolean {
  if (typeof status === 'number' && status > 0) {
    // BOUNDED to the real server range. `>= 500` alone would admit any
    // large number a fake or a future transport happened to report.
    return (status >= 500 && status <= 599) || RETRYABLE_STATUS.has(status);
  }
  if (!isObjectLike(error)) return false;
  // AuthRetryableFetchError is auth-js's OWN transient marker, so the name
  // alone is a reliable signal.
  const name = typeof error.name === 'string' ? error.name : '';
  if (name === 'AuthRetryableFetchError') return true;
  // `TypeError` deliberately does NOT qualify on its own. A browser `fetch`
  // rejects with TypeError when a request never completed — but so does
  // `Cannot read properties of undefined`, and retrying a programming defect
  // three times is exactly the 'unknown failures are permanent' rule being
  // broken by its own exception. The MESSAGE allowlist below is what
  // distinguishes 'TypeError: Failed to fetch' from a bug in this code.
  const message = typeof error.message === 'string' ? error.message : '';
  return TRANSPORT_FAILURE_RE.test(message);
}

// The three ways one attempt can end. Carried as a tagged value rather than by
// throwing, so the loop can tell a budget expiry (stop) from a transient
// transport failure (retry) without inspecting error shapes.
const TIMED_OUT = Symbol('session-profile-timeout');
const SUPERSEDED = Symbol('session-profile-superseded');

/**
 * Read the session profile within one bounded budget.
 *
 * Guarantees, in the order they matter:
 *   1. It never resolves to a profile it did not receive. A PostgREST error, a
 *      missing row, a timeout and a transport throw all end in `failed`.
 *   2. It ALWAYS SETTLES. The budget promise rejects on its own timer, so a
 *      request that ignores its abort signal — or never issues at all — cannot
 *      hang this function.
 *   3. At most one request is ISSUED at a time, and each attempt's controller
 *      is aborted before the next is constructed — best-effort cancellation,
 *      not proof that an abandoned request has stopped.
 *   4. No leaked timer or listener, and no unhandled rejection, on any exit.
 *      An abandoned request is parked and never awaited or published again.
 */
export async function loadSessionProfile<TProfile>({
  request,
  isCurrent,
  leaseSignal = null,
  attempts = SESSION_PROFILE_ATTEMPTS,
  timeoutMs = SESSION_PROFILE_TIMEOUT_MS,
  createController = () => new AbortController(),
}: LoadSessionProfileOptions<TProfile>): Promise<SessionProfileOutcome<TProfile>> {
  let lastError: unknown = null;
  let used = 0;

  // ONE budget, armed once, shared by every attempt. Deliberately a plain timer
  // rather than elapsed-time arithmetic: there is no clock to inject, nothing
  // to get wrong at a boundary, and a suite can advance it deterministically.
  let budgetTimer: ReturnType<typeof setTimeout> | null = null;
  const budget = new Promise<never>((_resolve, reject) => {
    budgetTimer = setTimeout(() => reject(TIMED_OUT), timeoutMs);
  });
  // The budget is raced, so on any non-timeout exit it stays rejected with
  // nobody listening. Park a handler now; without it a settled run leaves an
  // unhandled rejection behind.
  budget.catch(() => {});

  // Supersession is raced for the same reason the budget is: a request that
  // ignores its abort signal would otherwise keep a dead lease's run alive.
  let onLeaseAbort: (() => void) | null = null;
  const superseded = new Promise<never>((_resolve, reject) => {
    if (!leaseSignal) return;
    if (leaseSignal.aborted) {
      reject(SUPERSEDED);
      return;
    }
    onLeaseAbort = () => reject(SUPERSEDED);
    leaseSignal.addEventListener('abort', onLeaseAbort, {once: true});
  });
  superseded.catch(() => {});

  try {
    for (let attempt = 0; attempt < attempts; attempt++) {
      if (!isCurrent()) return {status: 'superseded'};
      used = attempt + 1;

      const controller = createController();
      // `abort()` on an already-aborted controller is a no-op, but a stubbed
      // controller in a suite need not be, so every call is guarded.
      const abortAttempt = (reason: Error) => {
        try {
          controller.abort(reason);
        } catch {
          /* abort is best-effort cancellation; the isCurrent guard is the correctness boundary */
        }
      };

      try {
        // The request promise is created here and NEVER awaited again after the
        // race settles. If it is the hung-forever case it simply stays pending;
        // parking a catch keeps a late rejection from surfacing as unhandled.
        const attemptRequest = Promise.resolve(request(controller.signal));
        attemptRequest.catch(() => {});
        const res = await Promise.race([attemptRequest, budget, superseded]);

        if (!isCurrent()) return {status: 'superseded'};
        // FAIL CLOSED, three ways. A PostgREST error is an error; a single-row
        // read that returned no row is an error (PGRST116 normally reports it,
        // and this covers the shape that does not); neither is ever an empty
        // profile the caller could mistake for a resolved identity.
        if (res && res.error) {
          lastError = res.error;
          // A permanent answer ends the resolution NOW. Asking a server that
          // already said 401 or PGRST116 two more times changes nothing except
          // how long the operator waits for the banner.
          if (!isRetryableProfileFailure(res.error, res.status)) {
            return {status: 'failed', error: res.error, attempts: used, timedOut: false};
          }
          continue;
        }
        if (!res || !res.data) {
          // Permanent by construction: a retry cannot conjure a row that the
          // single-row read did not return.
          return {
            status: 'failed',
            error: new Error('profile row missing'),
            attempts: used,
            timedOut: false,
          };
        }
        return {status: 'resolved', profile: res.data};
      } catch (error) {
        // A dead lease outranks everything: publish nothing.
        if (error === SUPERSEDED || !isCurrent()) return {status: 'superseded'};
        if (error === TIMED_OUT) {
          // A GENUINE TIMEOUT ENDS THE RESOLUTION. The budget is spent; further
          // attempts could only fail the same way, later.
          return {
            status: 'failed',
            error: new Error('profile load timeout'),
            attempts: used,
            timedOut: true,
          };
        }
        lastError = error;
        // A throw never reached a response, so there is no status to consult.
        // Only recognised transport shapes are worth another attempt.
        if (!isRetryableProfileFailure(error)) {
          return {status: 'failed', error, attempts: used, timedOut: false};
        }
      } finally {
        abortAttempt(new Error('session profile attempt finished'));
      }
    }
  } finally {
    if (budgetTimer !== null) clearTimeout(budgetTimer);
    if (onLeaseAbort && leaseSignal) leaseSignal.removeEventListener?.('abort', onLeaseAbort);
  }

  if (!isCurrent()) return {status: 'superseded'};
  return {
    status: 'failed',
    error: lastError || new Error('profile load failed'),
    attempts: used,
    timedOut: false,
  };
}

// ── The telemetry detail is ALLOWLISTED, not scrubbed ───────────────────────
//
// The earlier version stripped disallowed characters out of the value and kept
// the remainder. That is the wrong shape for this job: scrubbing turns an
// unrecognised value into a plausible-looking fragment and still emits it, so
// the thing that reaches `record_client_error` is derived from data nobody
// vetted. This matches the COMPLETE value against what a machine token can
// legitimately be, and emits nothing at all otherwise.
//
// Two forms, and no more than the real sources produce:
//   * an identifier — an error class name (`AuthApiError`), a PostgREST code
//     (`PGRST116`), a Supabase auth code (`refresh_token_not_found`);
//   * a five-character SQLSTATE (`53300`, `23505`).
//
// URLs, emails and row ids are excluded BY CONSTRUCTION rather than by a
// separate rule: `:` and `/` and `@` appear in neither form, and a UUID's
// hyphens are not in the identifier character class.
const MACHINE_TOKEN_RE = /^(?:[A-Za-z][A-Za-z0-9_]{0,39}|[0-9A-Z]{5})$/;

// A JWT is the one hostile shape the allowlist does NOT exclude on its own: a
// header segment such as `eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9` is 36 letters
// and digits starting with a letter, so it satisfies MACHINE_TOKEN_RE exactly.
// This rule is therefore load-bearing, not belt-and-braces.
const JWT_SEGMENT_RE = /^eyJ/;

/**
 * True only for a value safe to publish verbatim as a telemetry reason.
 * Exported so the rule itself is directly testable, rather than only
 * observable through the message it ends up in.
 */
export function isSafeMachineToken(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  if (!MACHINE_TOKEN_RE.test(value)) return false;
  if (JWT_SEGMENT_RE.test(value)) return false;
  return true;
}

/**
 * A short, allowlisted reason token for telemetry — NEVER the underlying error
 * message, and never a fragment of a value that failed the allowlist. Returns
 * '' rather than guessing when nothing safe is available.
 */
export function sessionProfileFailureDetail(error: unknown, timedOut: boolean): string {
  if (timedOut) return 'timeout';
  const code = isObjectLike(error) ? error.code : undefined;
  if (isSafeMachineToken(code)) return code;
  const name = isObjectLike(error) ? error.name : undefined;
  if (isSafeMachineToken(name)) return name;
  return '';
}

/**
 * The Error App throws (and reports) for a failed profile load.
 *
 * Its MESSAGE is synthesized from an attempt count and the allowlisted detail
 * above, so nothing derived from the response body — and therefore no JWT,
 * Authorization header, profile id, email or request URL — can reach
 * `record_client_error`.
 *
 * It carries NO `cause`. The reporter does not walk `cause` today, but the
 * telemetry boundary should not depend on that staying true: attaching the
 * raw provider error would put an unvetted object inside the value handed to
 * `reportError`, one serialization change away from leaking. The synthesized
 * message is the diagnostic.
 */
export function describeSessionProfileFailure(outcome: {error: unknown; attempts: number; timedOut: boolean}): Error {
  const detail = sessionProfileFailureDetail(outcome.error, outcome.timedOut);
  const failure = new Error(
    `session profile load failed after ${outcome.attempts} attempt(s)${detail ? ` [${detail}]` : ''}`,
  );
  failure.name = 'SessionProfileLoadError';
  return failure;
}
